import React from "react";
import { useState } from "react";
import { baseUrl } from '../shared.js';
import '../pages/App.css';
import {
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Link,
  IconButton,
  Tooltip,
} from "@mui/material";
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { AssetIdentity } from './ui';
import QrScanDialog from './pwa/QrScanDialog';

export function Search() {
  const [openSerialSearch, setOpenSerialSearch] = useState(false);
  const [lookupMode, setLookupMode] = useState('asset');
  const [scanOpen, setScanOpen] = useState(false);
  let [serialsFound, setSerialsFound] = useState([]);

    // In-app QR scan result → navigate the same way a camera-app deep link would.
    // assetNo (asset.assetId) preferred; fall back to catNo. Online page, so a
    // plain redirect resolves the asset via the existing ?assetNo / ?catNo routes.
    const handleScan = ({ assetNo, catNo }) => {
      setScanOpen(false);
      if (assetNo) window.location.href = '/?assetNo=' + encodeURIComponent(assetNo);
      else if (catNo) window.location.href = '/?catNo=' + encodeURIComponent(catNo);
    };

    function serialSearchOpenClose() {
      if(openSerialSearch) {
        setOpenSerialSearch(false)
      } else {
        setOpenSerialSearch(true)}
    }

    const handleSerialInputKeyDown = (e) => {
      if (e.keyCode === 13) {
        e.preventDefault();
        searchSerialNo();
      }
    };

    async function searchSerialNo() {
          const url = baseUrl + 'serialNo/' +document.getElementById("serialInput").value;
          await fetch(url, {
            method: 'GET',
            headers: {
                'username': localStorage.getItem('username'),
                'bToken': localStorage.getItem('bToken')
            }})
          .then((response) => response.json())
          .then((data) => {
            if(data.status === 401){
            } else {
             setSerialsFound(data.results);

            }
          })
          .catch((err) => {
             console.log(err.message);
          });
      };

    // Only navigate when there's an actual value — an empty lookup would hit
    // "/?assetNo=" which falls through to the dashboard instead of a result.
    const lookupAsset = () => {
      const v = (document.getElementById("assetInput")?.value || '').trim();
      if (!v) return;
      window.location.href = '/?assetNo=' + encodeURIComponent(v);
    };
    const lookupCatalogue = () => {
      const v = (document.getElementById("catalogueInput")?.value || '').trim();
      if (!v) return;
      window.location.href = '/?catNo=' + encodeURIComponent(v);
    };

    const canSerial =
      localStorage.getItem('canSearch') === '1' &&
      (localStorage.getItem('canEditAsset') === '1' || localStorage.getItem('canInspectAsset') === '1');

    return (
<>
    {/* ---- Serial number search dialog ---- */}
     <React.Fragment>
      <Dialog open={openSerialSearch} onClose={serialSearchOpenClose} maxWidth="sm" fullWidth>
        <DialogTitle>Search by Serial Number</DialogTitle>
        <DialogContent>
          {serialsFound.length === 0 ? (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                margin="dense"
                id="serialInput"
                label="Enter Serial Number to Search"
                variant="outlined"
                type="text"
                onKeyDown={handleSerialInputKeyDown}
              />
              <Button variant="contained" fullWidth sx={{ mt: 1 }} onClick={searchSerialNo}>
                Search Serial Number
              </Button>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Button variant="outlined" color="warning" fullWidth sx={{ mb: 1.5 }} onClick={() => setSerialsFound([])}>
                Clear Search
              </Button>
              <Box sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--faint)', mb: 1 }}>
                Serial Number Search Results
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {serialsFound.map((result, index) => (
                  <Box
                    key={index}
                    onClick={() => { window.location.href = result.foundURL; }}
                    sx={{
                      cursor: 'pointer',
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--r-sm)',
                      boxShadow: 'var(--shadow-sm)',
                      p: 1.5,
                      '&:hover': { borderColor: 'var(--primary)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                      <AssetIdentity asset={result} roomy idStyle={{ fontSize: 15 }} />
                    </Box>
                    <Box sx={{ fontSize: 12, color: 'var(--muted)', mt: 0.5 }}>
                      {[result.item, result.customer, result.siteLocation].filter(Boolean).join(' · ')}
                    </Box>
                    <Box className="aims-mono" sx={{ fontSize: 11, color: 'var(--faint)', mt: 0.25 }}>
                      Serial: {result.serialNo}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>

    {/* ---- Search panel ---- */}
  <Box sx={{ width: 'min(460px, calc(100vw - 32px))', mx: 'auto', mt: 1 }}>
    <Box sx={{ background: 'var(--surface)', borderRadius: 'var(--r)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', p: 2.5 }}>
      <Box sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', mb: 0.5 }}>Find an asset</Box>
      <Box sx={{ fontSize: 12, color: 'var(--muted)', mb: 2 }}>
        Search by asset, catalogue or serial number.
      </Box>

      {lookupMode === 'asset' ? (
        <>
          <TextField
            fullWidth
            margin="dense"
            id="assetInput"
            label="Asset Number"
            variant="outlined"
            type="text"
            onKeyDown={(e) => { if (e.keyCode === 13) { e.preventDefault(); lookupAsset(); } }}
          />
          <Button fullWidth variant="contained" sx={{ mt: 0.5 }} onClick={lookupAsset}>
            Lookup Asset Number
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => setLookupMode('catalogue')}
              sx={{ fontSize: 12, color: 'var(--muted)' }}
            >
              Switch to Catalogue Number Lookup
            </Link>
          </Box>
        </>
      ) : (
        <>
          <TextField
            fullWidth
            margin="dense"
            id="catalogueInput"
            label="Catalogue Number"
            variant="outlined"
            type="text"
            onKeyDown={(e) => { if (e.keyCode === 13) { e.preventDefault(); lookupCatalogue(); } }}
          />
          <Button fullWidth variant="contained" sx={{ mt: 0.5 }} onClick={lookupCatalogue}>
            Lookup Catalogue Number
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => setLookupMode('asset')}
              sx={{ fontSize: 12, color: 'var(--muted)' }}
            >
              Switch to Asset Number Lookup
            </Link>
          </Box>
        </>
      )}

      {canSerial ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5, color: 'var(--faint)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px' }}>
            <Box sx={{ flex: 1, height: '1px', background: 'var(--border)' }} /> or <Box sx={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Serial search reduced to ~85%; the freed ~15% (less the gap) is the QR scan launcher. */}
            <Button sx={{ flex: '1 1 85%' }} variant="outlined" onClick={() => { serialSearchOpenClose(); }}>
              Search by Serial Number
            </Button>
            <Tooltip title="Scan asset QR">
              <IconButton
                aria-label="Scan asset QR"
                onClick={() => setScanOpen(true)}
                sx={{
                  flex: '1 1 15%', borderRadius: 'var(--r-sm, 8px)',
                  border: '1px solid var(--border)', color: 'var(--primary)',
                  '&:hover': { borderColor: 'var(--primary)', background: 'var(--primary-tint)' },
                }}
              >
                <QrCodeScannerIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      ) : null}
    </Box>
  </Box>

  {/* In-app QR scanner (qr-scanner). Gated with the serial search by canSerial. */}
  <QrScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onResult={handleScan} />
</>
  )}

export default Search
